import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Button,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  CircularProgress,
  TableContainer,
  Box,
} from "@mui/material";
import { AddCircleOutline, Search, Edit, Delete } from "@mui/icons-material";
import FlexColumnBox from "../../components/common/FlexBox";
import EditHotel from "../../components/forms/EditHotel";
import AddHotel from "../../components/forms/AddHotel";
import HotelService from "../../api/hotelService";
import { Hotel } from "../../types/hotel.d";
import { useNavigate } from "react-router-dom";

const Hotels = () => {
  const [hotels, setHotels] = useState<Hotel[]>([]);
  const [contactsCount, setContactsCount] = useState<{
    [key: number]: number | null;
  }>({});
  const [searchTerm, setSearchTerm] = useState("");
  const [isInfoOpen, setIsInfoOpen] = useState(false);
  const [isAddHotelOpen, setIsAddHotelOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [selectedHotel, setSelectedHotel] = useState<Hotel | null>(null);
  const navigate = useNavigate();

  const filteredHotels = useMemo(
    () =>
      hotels.filter((hotel) =>
        hotel.name.toLowerCase().includes(searchTerm.toLowerCase())
      ),
    [searchTerm, hotels]
  );

  const fetchHotels = useCallback(async () => {
    try {
      const data = await HotelService.getAllHotels();
      setHotels(data);
      const initialCounts: { [key: number]: number | null } = {};
      data.forEach((hotel) => {
        initialCounts[hotel.id] = null;
      });
      setContactsCount(initialCounts);
      await fetchContactsCount();
    } catch (error) {
      console.error("Erreur de récupération des hôtels:", error);
    }
  }, []);

  const fetchContactsCount = async () => {
    try {
      const countsMap = await HotelService.getAllHotelsUserCount();
      setContactsCount(countsMap);
    } catch (error) {
      console.error(
        "Erreur lors de la récupération des contacts par hôtel :",
        error
      );
    }
  };

  const addHotelLocally = (newHotel: Hotel) => {
    setHotels((prev) => [...prev, newHotel]);
  };

  const updateHotelLocally = (updatedHotel: Hotel) => {
    setHotels((prev) =>
      prev.map((hotel) => (hotel.id === updatedHotel.id ? updatedHotel : hotel))
    );
  };

  const removeHotelLocally = (hotelId: number) => {
    setHotels((prev) => prev.filter((hotel) => hotel.id !== hotelId));
    setContactsCount((prev) => {
      const updated = { ...prev };
      delete updated[hotelId];
      return updated;
    });
  };

  useEffect(() => {
    fetchHotels();
  }, [fetchHotels]);

  const handleDeleteConfirm = async (deleteContacts: boolean) => {
    if (selectedHotel) {
      try {
        await HotelService.deleteHotel(selectedHotel.id, deleteContacts);
        removeHotelLocally(selectedHotel.id);
        setIsDeleteConfirmOpen(false);
      } catch (error) {
        console.error("Erreur lors de la suppression de l'hôtel:", error);
      }
    }
  };

  return (
    <>
      {selectedHotel && (
        <EditHotel
          isOpen={isInfoOpen}
          setIsOpen={setIsInfoOpen}
          hotel={selectedHotel}
          onSave={updateHotelLocally}
        />
      )}

      <AddHotel
        isOpen={isAddHotelOpen}
        setIsOpen={setIsAddHotelOpen}
        onHotelAdded={addHotelLocally}
      />

      <Dialog
        open={isDeleteConfirmOpen}
        onClose={() => setIsDeleteConfirmOpen(false)}
      >
        <DialogTitle>Confirmer la suppression</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {selectedHotel?.name} possède{" "}
            {contactsCount[selectedHotel?.id || 0] ?? 0} contact(s). Voulez-vous
            supprimer l'hôtel et les contacts associés ?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDeleteConfirmOpen(false)}>Annuler</Button>
          <Button
            onClick={() => handleDeleteConfirm(true)}
            color="error"
            variant="contained"
          >
            Supprimer avec les contacts
          </Button>
          <Button
            onClick={() => handleDeleteConfirm(false)}
            color="warning"
            variant="contained"
          >
            Supprimer seulement l'hôtel
          </Button>
        </DialogActions>
      </Dialog>

      <FlexColumnBox gap={2} mt={2} mb={5} alignItems="center" width="100%">
        <Typography variant="h5">Liste des hôteliers</Typography>

        <Box maxWidth={400} width="100%">
          <TextField
            fullWidth
            placeholder="Recherchez un hôtel..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <TableContainer
          component={Paper}
          sx={{ width: "100%", maxWidth: 1000, overflowX: "auto" }}
        >
          <Table size="small" sx={{ minWidth: 700 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: "40%" }}>
                  <Typography fontWeight="bold">Hôtel</Typography>
                </TableCell>
                <TableCell align="right" sx={{ width: "20%" }}>
                  <Typography fontWeight="bold">Chambres</Typography>
                </TableCell>
                <TableCell align="right" sx={{ width: "20%" }}>
                  <Typography fontWeight="bold">Contacts</Typography>
                </TableCell>
                <TableCell align="right" sx={{ width: "20%" }}>
                  <Typography fontWeight="bold">Actions</Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredHotels.map((hotel) => (
                <TableRow hover key={hotel.id}>
                  <TableCell
                    sx={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {hotel.name}
                  </TableCell>
                  <TableCell align="right">{hotel.maxUnitsCapacity}</TableCell>
                  <TableCell align="right">
                    {contactsCount[hotel.id] === null ? (
                      <CircularProgress size={20} />
                    ) : (
                      <Button
                        size="small"
                        variant="text"
                        onClick={() =>
                          navigate("/contacts", {
                            state: { hotelId: hotel.id },
                          })
                        }
                      >
                        {contactsCount[hotel.id]}
                      </Button>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      onClick={() => {
                        setSelectedHotel(hotel);
                        setIsInfoOpen(true);
                      }}
                    >
                      <Edit />
                    </IconButton>
                    <IconButton
                      color="error"
                      onClick={() => {
                        setSelectedHotel(hotel);
                        setIsDeleteConfirmOpen(true);
                      }}
                    >
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {filteredHotels.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    Aucun hôtel trouvé.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Button
          variant="contained"
          endIcon={<AddCircleOutline />}
          onClick={() => setIsAddHotelOpen(true)}
        >
          Ajouter un hôtel
        </Button>
      </FlexColumnBox>
    </>
  );
};

export default Hotels;
